/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { Typography2, breakpoints } from '@coursera/cds-core';

import type { CareerRole } from 'bundles/degree-description/components/modules/career-outcomes/CareerOutcomesModule';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

type Props = {
  roles: CareerRole[];
};

const ROLE_IMAGE_SIZE = 64;

const styles = {
  carousel: css`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 85%;
    gap: var(--cds-spacing-150);
    width: 100%;

    ${breakpoints.up('sm')} {
      grid-auto-columns: 45%;
    }

    ${breakpoints.up('md')} {
      grid-auto-columns: 23%;
    }
  `,
  card: css`
    display: flex;
    align-items: flex-start;
    gap: var(--cds-spacing-150);
    height: 100%;
    padding: var(--cds-spacing-150);
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-100);
  `,
  imagePlaceholder: css`
    flex-shrink: 0;
    width: ${ROLE_IMAGE_SIZE}px;
    height: ${ROLE_IMAGE_SIZE}px;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
  imageWrapper: css`
    flex-shrink: 0;
    width: ${ROLE_IMAGE_SIZE}px;
    height: ${ROLE_IMAGE_SIZE}px;
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;

    img {
      object-fit: cover;
    }
  `,
  cardBody: css`
    min-width: 0;
  `,
};

const RolesCarousel = ({ roles }: Props) => {
  if (!roles.length) return null;

  return (
    <OverflowCarousel css={styles.carousel}>
      {roles.map((role) => (
        <div key={role.id} css={styles.card}>
          {role.imageUrl ? (
            <div css={styles.imageWrapper}>
              <LazyImgix
                src={role.imageUrl}
                maxWidth={ROLE_IMAGE_SIZE}
                maxHeight={ROLE_IMAGE_SIZE}
                enableFluidWidth
                alt=""
              />
            </div>
          ) : (
            <div css={styles.imagePlaceholder} aria-hidden="true" />
          )}
          <div css={styles.cardBody}>
            <Typography2 variant="subtitleMedium" component="p">
              {role.title}
            </Typography2>
            <Typography2 variant="bodySecondary" component="p" color="supportText">
              {role.description}
            </Typography2>
          </div>
        </div>
      ))}
    </OverflowCarousel>
  );
};

export default React.memo(RolesCarousel);
